import { Button, FullScreenLoading } from '@civilgrid/ui/components';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { addTransaction as apiAddTransaction } from '~/api/internal';
import { useQuery } from '~/common/routerUtils';
import { type NewTransactionData, TransactionCategory } from '~/common/types';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import Calendar from '../common/Calendar';
import { toNumber, validateDecimal } from '../common/common';
import Field from '../common/Field';
import { MajorHeading } from '../common/Heading';
import SelectField from '../common/SelectField';
import { type TransactionFields, transactionCategories, transactionTypes } from './common';

export default function Transactions() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const defaultValues: TransactionFields = {
    projectId: '',
    amount: 0,
    description: '',
    type: 'debit',
    category: TransactionCategory.Other,
    transactionDate: new Date().toISOString(),
  };

  const { handleSubmit, control, reset, setValue } = useForm<TransactionFields>({
    defaultValues,
  });
  const query = useQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const _data = query.get('data');
    if (_data) {
      const parsedData = JSON.parse(decodeURI(_data));
      reset({
        ...defaultValues,
        ...parsedData,
      });
    }
  }, [query]);

  const onSubmit: SubmitHandler<TransactionFields> = async (data) => {
    try {
      setIsLoading(true);

      const transactionData: NewTransactionData = {
        projectId: data.projectId,
        amount: toNumber(data.amount),
        description: data.description,
        type: data.type,
        category: data.category,
        transactionDate: data.transactionDate,
      };

      const res = await apiAddTransaction(transactionData);

      if (res?.ok !== true) {
        throw new Error('Error adding transaction');
      }

      Notify.info({
        title: 'Success',
        message: 'New transaction successfully added',
      });

      reset();

      navigate({ to: '/projects/$projectId/$tabId', params: { projectId: data.projectId, tabId: 'billing' } });
    } catch {
      Notify.error({
        title: 'Error',
        message: 'Error adding transaction',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <FullScreenLoading />;
  }

  return (
    <div className="space-y-4">
      <MajorHeading>Add Transaction</MajorHeading>
      <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Field
          id="projectId"
          label="Enter 24-digit Project ID"
          control={control}
          placeholder="Ex: 65133a847c0cb0536a2ef580"
          tooltipText='This can be found in the browser URL in the "Project Details" page.'
        />

        <Field
          id="description"
          label="Transaction Description"
          control={control}
          placeholder="Add a detailed transaction description here"
        />

        <Calendar
          control={control}
          name="transactionDate"
          label="Transaction Date"
          current={new Date().toISOString()}
          setValue={setValue}
        />

        <Field
          id="amount"
          label="Enter Transaction Amount in Cents"
          control={control}
          type="number"
          rules={validateDecimal}
          tooltipText="Must be a positive, whole number"
        />

        <SelectField id="type" label="Transaction Type" control={control} items={transactionTypes} />

        <SelectField id="category" label="Transaction Category" control={control} items={transactionCategories} />

        <Button type="submit">Add Transaction</Button>
      </Form>
    </div>
  );
}
